import axios from "axios";

// ── Job Queries (via Production Scheduling) ──────────────────

/**
 * Get schedulable jobs for a specific production stage.
 * GET /v1/production/schedule/jobs/:stageCode
 *
 * @param {string} stageCode - e.g. "corrugation", "printing"
 * @param {object} params - { priority, customer_id, fulfillment_status }
 * @returns {{ stage, total, jobs[] }}
 */
export const getJobsByStage = async (stageCode, params = {}) => {
  const response = await axios.get(
    `production/schedule/jobs/${stageCode}`,
    { params }
  );
  return response.data;
};

/**
 * Get all jobs with optional filters.
 * Uses the direct /v1/job endpoint which returns all jobs regardless of stage.
 *
 * @param {object} filters - { priority, customer_id, fulfillment_status, stage, status, search }
 * @returns {{ stages[], jobs[], totalJobs }}
 */
export const getAllJobs = async (filters = {}) => {
  // 1) Get the default production flow for stage metadata
  let stages = [];
  try {
    const flow = await axios.get("production/flow/default").then((r) => r.data);
    stages = flow.stages || [];
  } catch (e) {
    console.warn("Could not load production flow stages", e.message);
  }

  // 2) Build query params for the jobs endpoint
  const params = {};
  if (filters.priority) params.priority = filters.priority;
  if (filters.status) params.status = filters.status;
  if (filters.search) params.search = filters.search;
  if (filters.customer_id) params.customer_id = filters.customer_id;
  params.page = 1;
  params.count = 500;

  // 3) Fetch all jobs from the direct endpoint
  const response = await axios.get("job", { params });
  const allJobs = (response.data?.data || response.data?.getAllJob || response.data?.jobs || []);

  // 4) Enrich each job with stage name/color from the flow
  const stageMap = {};
  stages.forEach((s) => { stageMap[s.code] = s; });

  const enrichedJobs = (Array.isArray(allJobs) ? allJobs : []).map((job) => {
    const stageInfo = stageMap[job.current_stage];
    return {
      ...job,
      current_stage_name: stageInfo?.name || job.current_stage,
      stage_color: stageInfo?.color || "#6366F1",
    };
  });

  // 5) Filter by stage if requested
  const finalJobs = filters.stage && filters.stage !== "all"
    ? enrichedJobs.filter((j) => j.current_stage === filters.stage)
    : enrichedJobs;

  return {
    stages,
    jobs: finalJobs,
    totalJobs: finalJobs.length,
  };
};

// ── Scheduling ───────────────────────────────────────────────

/**
 * Schedule a job to a machine for a specific stage.
 * POST /v1/production/schedule/job
 *
 * @param {{ job_id, stage_id, machine_id, scheduled_date, start_time }} data
 */
export const scheduleJob = async (data) => {
  const response = await axios.post("production/schedule/job", data);
  return response.data;
};

/**
 * Get a single job by ID with order_item details.
 * GET /v1/job/:id
 */
export const getJobById = async (id) => {
  const response = await axios.get(`job/${id}`);
  return response.data;
};

export const updateJob = async (jobId, data) => {
  const response = await axios.put(`job/${jobId}`, data);
  return response.data;
};

export const calculateJobSpecs = async (data) => {
  const response = await axios.post("job/calculate-specs", data);
  return response.data;
};

/**
 * Reschedule an existing schedule entry.
 * PUT /v1/production/schedule/:id/reschedule
 *
 * @param {string} scheduleId
 * @param {{ start_time, machine_id }} data
 */
export const rescheduleJob = async (scheduleId, data) => {
  const response = await axios.put(
    `production/schedule/${scheduleId}/reschedule`,
    data
  );
  return response.data;
};

/**
 * Cancel a schedule entry.
 * PUT /v1/production/schedule/:id/cancel
 */
export const cancelSchedule = async (scheduleId) => {
  const response = await axios.put(
    `production/schedule/${scheduleId}/cancel`
  );
  return response.data;
};

/**
 * Get Gantt chart data for a date range.
 * GET /v1/production/schedule/gantt
 *
 * @param {{ start_date, end_date, machine_ids? }} params
 */
export const getGanttData = async (params) => {
  const response = await axios.get("production/schedule/gantt", { params });
  return response.data;
};

/**
 * Get capacity utilization data.
 * GET /v1/production/schedule/capacity
 *
 * @param {{ start_date, end_date, machine_ids? }} params
 */
export const getCapacityUtilization = async (params) => {
  const response = await axios.get("production/schedule/capacity", {
    params,
  });
  return response.data;
};

// ── Batch Management ─────────────────────────────────────────

/**
 * Get batch suggestions for a stage.
 * GET /v1/production/schedule/batch-suggestions/:stageCode
 */
export const getBatchSuggestions = async (stageCode) => {
  const response = await axios.get(
    `production/schedule/batch-suggestions/${stageCode}`
  );
  return response.data;
};

/**
 * Create a production batch.
 * POST /v1/production/schedule/batch
 *
 * @param {{ stage_id, job_ids[], machine_id }} data
 */
export const createBatch = async (data) => {
  const response = await axios.post("production/schedule/batch", data);
  return response.data;
};

/**
 * Get paginated batches.
 * GET /v1/production/schedule/batch
 *
 * @param {{ status, stage_id, page, limit }} params
 */
export const getBatches = async (params = {}) => {
  const response = await axios.get("production/schedule/batch", { params });
  return response.data;
};

/**
 * Get batch by ID.
 * GET /v1/production/schedule/batch/:id
 */
export const getBatchById = async (id) => {
  const response = await axios.get(`production/schedule/batch/${id}`);
  return response.data;
};

/**
 * Schedule a batch.
 * POST /v1/production/schedule/batch/:batchId
 */
export const scheduleBatch = async (batchId, data) => {
  const response = await axios.post(
    `production/schedule/batch/${batchId}`,
    data
  );
  return response.data;
};

// ── Production Instructions ──────────────────────────────────

/**
 * Generate a production instruction sheet for a job + stage.
 * POST /v1/production/instruction/generate
 *
 * @param {{ job_id, stage_code }} data
 */
export const generateInstruction = async (data) => {
  const response = await axios.post(
    "production/instruction/generate",
    data
  );
  return response.data;
};

/**
 * Get instruction by ID.
 * GET /v1/production/instruction/:id
 */
export const getInstructionById = async (id) => {
  const response = await axios.get(`production/instruction/${id}`);
  return response.data;
};

/**
 * Get all instructions for a specific job.
 * GET /v1/production/instruction/job/:jobId
 */
export const getInstructionsForJob = async (jobId) => {
  const response = await axios.get(`production/instruction/job/${jobId}`);
  return response.data;
};

/**
 * Get printable instruction data.
 * GET /v1/production/instruction/:id/print
 */
export const printInstruction = async (id) => {
  const response = await axios.get(`production/instruction/${id}/print`);
  return response.data;
};

/**
 * Complete an instruction (record output/wastage).
 * POST /v1/production/instruction/:id/complete
 *
 * @param {string} instructionId
 * @param {{ actual_output, rejected_qty, wastage_qty, notes, attachments? }} data
 */
export const completeInstruction = async (instructionId, data) => {
  const response = await axios.post(
    `production/instruction/${instructionId}/complete`,
    data
  );
  return response.data;
};

// ── MRP ──────────────────────────────────────────────────────

/**
 * Calculate / preview BOM for a job.
 * POST /v1/mrp/bom/:jobId
 */
export const getJobBOM = async (jobId) => {
  const response = await axios.post(`mrp/bom/${jobId}`);
  return response.data;
};

/**
 * Run MRP for a job (saves requirements).
 * POST /v1/mrp/run/:jobId
 */
export const runMRP = async (jobId) => {
  const response = await axios.post(`mrp/run/${jobId}`);
  return response.data;
};

/**
 * Get material requirements for a job.
 * GET /v1/mrp/job/:jobId/requirements
 */
export const getJobRequirements = async (jobId) => {
  const response = await axios.get(`mrp/job/${jobId}/requirements`);
  return response.data;
};

// ── Machines (read-only helpers) ─────────────────────────────

/**
 * Get all machines.
 * GET /v1/machine
 */
export const getAllMachines = async (params = {}) => {
  const response = await axios.get("machine", { params });
  return response.data;
};

/**
 * Get machines filtered by type.
 * GET /v1/machine/type/:type
 */
export const getMachinesByType = async (type) => {
  const response = await axios.get(`machine/type/${type}`);
  return response.data;
};

/**
 * Find best machine for given constraints.
 * GET /v1/machine/find-best
 */
export const findBestMachine = async (params) => {
  const response = await axios.get("machine/find-best", { params });
  return response.data;
};

// ── Machine Selection for Job ────────────────────────────────

/**
 * Auto-select machines for all stages of a job.
 * POST /v1/job/:id/auto-select-machines
 */
/**
 * Advance job to next stage (checks inventory).
 * POST /v1/job/:id/advance-stage
 */
export const advanceJobStage = async (jobId) => {
  const response = await axios.post(`job/${jobId}/advance-stage`);
  return response.data;
};

/**
 * Move job to any target stage (for kanban DnD). Skipped stages are recorded.
 * POST /v1/job/:id/move-stage
 */
export const moveJobToStage = async (jobId, targetStage, stageCompletion = null) => {
  const payload = { target_stage: targetStage };
  if (stageCompletion) payload.stage_completion = stageCompletion;
  const response = await axios.post(`job/${jobId}/move-stage`, payload);
  return response.data;
};

export const autoSelectMachines = async (jobId) => {
  const response = await axios.post(`job/${jobId}/auto-select-machines`);
  return response.data;
};

/**
 * Get available machines for a specific stage.
 * GET /v1/job/:id/available-machines/:stageCode
 */
export const getAvailableMachines = async (jobId, stageCode, machineType) => {
  const params = machineType ? { machine_type: machineType } : {};
  const response = await axios.get(`job/${jobId}/available-machines/${stageCode}`, { params });
  return response.data;
};

/**
 * Update machine selection for a specific stage.
 * PUT /v1/job/:id/machine-selection
 */
export const updateMachineSelection = async (jobId, stageCode, machineId, machineType = null) => {
  const response = await axios.put(`job/${jobId}/machine-selection`, {
    stage_code: stageCode,
    machine_id: machineId,
    machine_type: machineType,
  });
  return response.data;
};

// ── Outsource (read-only helpers) ────────────────────────────

/**
 * Get outsource jobs linked to a production job.
 * GET /v1/outsource/job/:jobId
 */
export const getOutsourceForJob = async (jobId) => {
  const response = await axios.get(`outsource/job/${jobId}`);
  return response.data;
};

// ── Wastage ──────────────────────────────────────────────────

/**
 * Get wastage summary for a job.
 * GET /v1/wastage/job/:jobId
 */
export const getJobWastageSummary = async (jobId) => {
  const response = await axios.get(`wastage/job/${jobId}`);
  return response.data;
};

export const applyBatchSuggestion = async (jobId, suggestedRs, newBoxesPerSheet) => {
  const response = await axios.post(`job/${jobId}/apply-suggestion`, {
    suggested_rs: suggestedRs,
    new_boxes_per_sheet: newBoxesPerSheet,
  });
  return response.data;
};

export const revertBatchSuggestion = async (jobId) => {
  const response = await axios.post(`job/${jobId}/revert-suggestion`);
  return response.data;
};

export const rejectBatchSuggestion = async (jobId, suggestedRs) => {
  const response = await axios.post(`job/${jobId}/reject-suggestion`, {
    suggested_rs: suggestedRs,
  });
  return response.data;
};

export const applyBoxReductionSuggestion = async (jobId, option) => {
  const response = await axios.post(`job/${jobId}/apply-box-reduction`, {
    option,
  });
  return response.data;
};

export const rejectBoxReductionSuggestion = async (jobId) => {
  const response = await axios.post(`job/${jobId}/reject-box-reduction`);
  return response.data;
};

export default {
  // Job queries
  getJobsByStage,
  getAllJobs,
  // Scheduling
  scheduleJob,
  rescheduleJob,
  cancelSchedule,
  getGanttData,
  getCapacityUtilization,
  // Batches
  getBatchSuggestions,
  createBatch,
  getBatches,
  getBatchById,
  scheduleBatch,
  // Instructions
  generateInstruction,
  getInstructionById,
  getInstructionsForJob,
  printInstruction,
  completeInstruction,
  // MRP
  getJobBOM,
  runMRP,
  getJobRequirements,
  // Machines
  getAllMachines,
  getMachinesByType,
  findBestMachine,
  autoSelectMachines,
  getAvailableMachines,
  updateMachineSelection,
  // Outsource
  getOutsourceForJob,
  // Wastage
  getJobWastageSummary,
};
